import { BufferGeometry, BufferGeometryLoader, Mesh, MeshPhongMaterial, Quaternion } from "three";
import { Config } from "../../Config/Config.js";
import { ViewType } from "../../Models/AppModel.js";
import { bindedModelBase } from "../../Models/BindedModel.js";
import { AnatomicalSideEnum, BoneTypeEnum, ReferenceTypeEnum } from "../../Models/CaseModel.js";
import { RingTypeEnum } from "../../Models/RingModel.js";
import { AxialRotations } from "../../Utils/AxialRotations.js";
import { Consts } from "../../Utils/Consts.js";
import { Mastertab } from "./Mastertab.js";
import { Hitbox } from "@ortho-next/three-base";
const loader = new BufferGeometryLoader();
export var RingSide;
(function (RingSide) {
    RingSide[RingSide["reference"] = 0] = "reference";
    RingSide[RingSide["secondary"] = 1] = "secondary";
})(RingSide || (RingSide = {}));
/**
 * Create the ring preview model.
 */
export class CalibrationRing extends Mesh {
    constructor(viewType) {
        super(Consts.emptyGeometry, new MeshPhongMaterial({ color: Config.calib_ring_colorHex, transparent: true, opacity: Config.calib_ring_opacity }));
        this.angle = 0;
        this.frontal = 0;
        this.axial = 0;
        this.axialTypeOffset = 0;
        this.axialMastertabOffset = 0;
        this._defaultRotation = new Quaternion().setFromAxisAngle(Consts.horDir, -Math.PI / 2);
        this.add(this.mastertab = new Mastertab(viewType));
        const isRegisteringClick = viewType === ViewType.AP ? "isRegistering2ClickAP" : "isRegistering2ClickLT";
        this.interceptedByRaycaster = true; // to make mastertab works
        this.registerHitbox(new Hitbox(new BufferGeometry()));
        this.scale.setScalar(1000);
        this.bindProperty("visible", (m) => { return !m[isRegisteringClick] && m.garageRingLoaded; }, [isRegisteringClick, "garageRingLoaded"]);
    }
    async setRing(ringModel, oppositeRingType) {
        this.ringModel = ringModel;
        this.oppositeRingType = oppositeRingType;
        this.diameter = parseInt(this.ringModel?.description.substring(0, 3));
        this.radius = this.diameter / 2;
        if (!ringModel) {
            this.geometry = Consts.emptyGeometry;
        }
        else {
            await this.load();
        }
        this.updateInitialAxialRotation();
        this.mastertab.update(ringModel?.ringType, oppositeRingType);
        this.needsRender = true;
    }
    async load() {
        return new Promise((resolve, reject) => {
            loader.load(this.getRingName(), (geometry) => {
                bindedModelBase.garageRingLoaded = true;
                this.geometry = geometry;
                geometry.computeVertexNormals();
                resolve();
            }, undefined, (err) => {
                console.error(err);
                reject();
            });
        });
    }
    getRingName() {
        let type;
        switch (this.ringModel.ringType) {
            case RingTypeEnum.OpenAnteriorRing:
            case RingTypeEnum.OpenMedialRing:
            case RingTypeEnum.OpenPosteriorRing:
                type = "5_8";
                break;
            case RingTypeEnum.FullRing:
            case RingTypeEnum.FullRing58Plus38:
                type = "full_ring";
                break;
            case RingTypeEnum.FootPlate:
                type = "foot_plate";
                break;
        }
        return `${Consts.cdnUrl}/templates/tlhex/${type}_${this.diameter}_export.json`;
    }
    updateInitialAxialRotation() {
        const ringType = this.ringModel?.ringType;
        const oppositeRingType = this.oppositeRingType;
        if (!ringType || !oppositeRingType) {
            this.axialTypeOffset = 0;
            this.axialMastertabOffset = 0;
            return;
        }
        const sign = this.side === AnatomicalSideEnum.Left ? -1 : 1;
        this.axialTypeOffset = this.getAxialTypeOffset(ringType, oppositeRingType);
        this.axialMastertabOffset = AxialRotations.getReference(this.boneType, this.refType, this.side, ringType, oppositeRingType) * sign;
    }
    getAxialTypeOffset(ringType, oppositeRingType) {
        const boneType = this.boneType;
        const side = this.side;
        const isProximalCase = this.refType === ReferenceTypeEnum.Proximal;
        const isDistalRing = !isProximalCase; //this is always reference ring
        const isProximalRing = !isDistalRing;
        let rotation = 0; // remove this var
        switch (ringType) {
            case RingTypeEnum.OpenAnteriorRing:
                return rotation -= Math.PI / 2;
            case RingTypeEnum.OpenMedialRing:
                return rotation += side === AnatomicalSideEnum.Left ? 0 : Math.PI;
            case RingTypeEnum.OpenPosteriorRing:
                return rotation += Math.PI / 2;
            case RingTypeEnum.FootPlate:
                // Double footplate: longleg, femur, tibia, rotate distal ring; ankle, foot, rotate proximal ring. 
                const longlegCases = boneType === BoneTypeEnum.LongLeg || boneType === BoneTypeEnum.Tibia || boneType === BoneTypeEnum.Femur;
                return rotation += Math.PI / ((oppositeRingType === RingTypeEnum.FootPlate && isDistalRing == longlegCases) ? -2 : 2);
            case RingTypeEnum.FullRing:
            case RingTypeEnum.FullRing58Plus38:
                if (isProximalRing) {
                    return rotation += oppositeRingType === RingTypeEnum.OpenMedialRing ? (side === AnatomicalSideEnum.Left ? Math.PI / 3 : 0) :
                        (oppositeRingType === RingTypeEnum.OpenPosteriorRing ? Math.PI / 6 : Math.PI / 2);
                }
                if (oppositeRingType === RingTypeEnum.OpenMedialRing)
                    return rotation += (side === AnatomicalSideEnum.Left ? 0 : Math.PI / 3);
                if (oppositeRingType === RingTypeEnum.OpenPosteriorRing)
                    return rotation += Math.PI / 6 + Math.PI / 3;
                if (oppositeRingType === RingTypeEnum.OpenAnteriorRing)
                    return rotation += Math.PI / 2 - Math.PI / 3;
                if (oppositeRingType === RingTypeEnum.FootPlate)
                    return rotation -= Math.PI / 2;
                return rotation += Math.PI / 2;
        }
    }
    update(center, scale, angle, frontal, axial) {
        this.angle = angle;
        this.frontal = frontal;
        this.axial = axial;
        const angleAxis = Consts.planeNormal.clone().applyQuaternion(this._defaultRotation);
        const frontalAxis = Consts.horDir.clone().applyQuaternion(this._defaultRotation);
        const axialAxis = Consts.verDir.clone().applyQuaternion(this._defaultRotation);
        this.setRotationFromQuaternion(this._defaultRotation);
        this.rotateOnAxis(angleAxis, this.angle);
        this.rotateOnAxis(frontalAxis, this.frontal);
        this.rotateOnAxis(axialAxis, this.axial + this.axialTypeOffset + this.axialMastertabOffset);
        this.position.copy(center);
        this.scale.setScalar(1000 * scale);
    }
}
//# sourceMappingURL=CalibrationRing.js.map